import { useRouter } from 'next/navigation';
import { useMemo, useState } from 'react';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { useElasticProductsSearch } from 'src/hooks/useElasticProductsSearch';
import { useFilteredProducts } from 'src/hooks/useFilteredProducts';
import { ProductsSectionProps } from 'src/layout/Header/types';
import { PLATFORMS } from 'src/mocks/products/platforms';

import { SectionCatalogMain } from '../../../../components/SectionCatalogMain/src';
import { useLayoutType } from '../../../../hooks/useLayoutType';
import { CatalogProductType } from '../../../../types/backendContent';
import { ComplexSearchOffer } from './components/ComplexSearchOffer';
import s from './Products.module.scss';
import { createFooterLinks } from './utils/createFooterLinks';
import { mapProductWithCategories } from './utils/mapProductWithCategories';

function ProductsHeader({ products, onClickForClose }: ProductsSectionProps) {
  const { clickAnalytics, customAnalytics, searchResultClickAnalytics } =
    useAnalytics();
  const {
    filteredProducts,
    platformFilter,
    titleFilter,
    setPlatformFilter,
    setTitleFilter,
    categoriesByPlatform,
  } = useFilteredProducts({
    initialPlatformFilter: PLATFORMS[0].slug,
    initialCategoryFilter: products.categories[0].slug,
    categories: products.categories,
    products: products.items,
  });

  const { searched } = useElasticProductsSearch({
    phrase: titleFilter.trim(),
    products: products.items,
    filteredProducts: filteredProducts,
    platform: platformFilter,
    typeAnalytics: 'live-menu-products',
  });

  const [activeCategory, setActiveCategory] = useState(
    categoriesByPlatform[0].id,
  );

  const layoutType = useLayoutType();

  const router = useRouter();

  const handleCategoryFilterChange = (categoryId: string) => {
    const foundCategory =
      categoriesByPlatform.find((item) => item.id === categoryId)?.title ??
      categoryId;
    setActiveCategory(categoryId);
    clickAnalytics({
      action: 'click',
      clickZone: 'header',
      clickElement: 'filter-select',
      clickContent: foundCategory,
      uniqueId: `header-menu-products-categories-filter-select-${foundCategory}`,
      transitionType: 'inside-link',
      clickZoneCategory: 'site-header',
      transitionTypeMetrics: 'internal',
    });
  };

  const handlePlatformFilterChange = (platform?: string) => {
    if (platform) {
      clickAnalytics({
        action: 'click',
        clickZone: 'header',
        clickElement: 'filter-select',
        clickContent: platform,
        uniqueId: `header-menu-products-platform-filter-select-${platform}`,
        transitionType: 'inside-link',
        clickZoneCategory: 'site-header',
        transitionTypeMetrics: 'internal',
      });
    }
    setActiveCategory(categoriesByPlatform[0].id);
    setPlatformFilter(platform);
  };

  const handleFocusSearch = () => {
    clickAnalytics({
      action: 'click',
      clickZone: 'header',
      clickElement: 'input',
      clickContent: 'Поиск продукта',
      uniqueId: 'header-menu-products-search-input',
      transitionType: 'inside-link',
      clickZoneCategory: 'site-header',
      transitionTypeMetrics: 'internal',
    });
  };

  const onChangeSection = () => {
    setTitleFilter('');
  };

  const handleChangeFilter = (value: string) => {
    value.trim().length > 180
      ? setTitleFilter(value.slice(0, 180))
      : setTitleFilter(value);
  };

  const productCategoriesMap = useMemo(
    () =>
      mapProductWithCategories({
        categoriesByPlatform,
        titleFilter,
        platformFilter,
        searched,
        productsItems: products.items,
        onClickElement: (
          href: string,
          title: string,
          productType: CatalogProductType,
          analyticSource,
          requestSearchId,
          positionNumber,
        ) => {
          //TODO: удалить customAnalytics после тестирования
          if (productType === 'solutions') {
            customAnalytics('AB_solutions_card_clicks', ['']);
          }

          if (titleFilter.length > 0) {
            searchResultClickAnalytics({
              link: href,
              searchPhrase: titleFilter,
              type: 'live-page-products',
              source: analyticSource ?? 'filter',
              category: 'site-body',
              requestId: requestSearchId ?? 'placeholder',
              positionNumber: positionNumber,
            });
          } else {
            clickAnalytics({
              action: href,
              clickZone: 'header',
              clickElement: 'cardclick',
              clickContent: title,
              uniqueId: `catalog-product-${title}`,
              transitionType: 'inside-link',
              clickZoneCategory: 'site-header',
              transitionUrl: href,
              transitionTypeMetrics: 'internal',
            });
          }

          if (href.startsWith('/')) {
            router.push(href);
          }
        },
      }),
    [
      categoriesByPlatform,
      clickAnalytics,
      customAnalytics,
      platformFilter,
      products.items,
      router,
      searchResultClickAnalytics,
      searched,
      titleFilter,
    ],
  );

  const allSolutionsLength =
    products.items?.filter((product) => product.type === 'solutions').length ??
    0;
  const allProductsLength =
    products.items?.filter((product) => product.type === 'products').length ??
    0;

  return (
    <div className={s.root}>
      <SectionCatalogMain
        layoutType={layoutType}
        onClickForClose={onClickForClose}
        searchProps={{
          handleFocusSearch,
          handleChangeFilter,
          onPlatformFilterChange: handlePlatformFilterChange,
          titleFilter,
          platforms: PLATFORMS,
          platformFilter,
        }}
        sectionCatalogProps={{
          id: 'SectionCatalogMainHeader',
          categories: productCategoriesMap,
          onCategorySelect: handleCategoryFilterChange,
          selectedCategory: activeCategory,
          layoutType,
          footer: createFooterLinks({
            allProductsLength,
            allSolutionsLength,
            clickAnalytics,
            customAnalytics,
            router,
          }),
          platformFilter,
          isSearching: titleFilter.trim().length > 0,
          onChangeSection,
          bottomContent:
            titleFilter.trim().length > 0 ? (
              <ComplexSearchOffer
                titleFilter={titleFilter.trim()}
                onClickForClose={onClickForClose}
              />
            ) : undefined,
        }}
      />
    </div>
  );
}

export function Products({ products, onClickForClose }: ProductsSectionProps) {
  return (
    <ProductsHeader products={products} onClickForClose={onClickForClose} />
  );
}
